import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApproveRun } from "~/api/queries";
import type { Dispatch } from "~/api/types";
import { Button } from "~/foundation/components/Button";

/**
 * The approvals slate can actually answer.
 *
 * There are two kinds, and only one of them has a REST verb (§E-3). A **run** approval is a
 * dispatched flow parked on a `requireApproval` node — it is answered with
 * `POST /api/runs.approve` and works with no socket at all, so it ships. A **hub** approval is
 * raised by TomatoFlow's `ApprovalService` into the conversation's SignalR group and can only
 * be answered by invoking `RespondToApproval` on that hub; slate does not join it, so nothing
 * is rendered for one. A dead button would be worse than the sentence below it.
 *
 * Three properties of `runs.approve` the surface must preserve: it is **not a control verb**
 * (no debugger step can release a gate), the **first answer wins wherever it came from**, and
 * the **channel forces the label** — a browser click is recorded as `ui`, always.
 */
export function ApprovalCards({ dispatches }: { dispatches: Dispatch[] }) {
  const { t } = useTranslation();
  const approve = useApproveRun();

  const parked = dispatches.filter(
    (dispatch) => dispatch.runId && dispatch.run?.awaitingApprovalNodeId,
  );

  if (parked.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {parked.map((dispatch) => {
        const nodeId = dispatch.run?.awaitingApprovalNodeId ?? "";
        const decide = (approved: boolean) =>
          approve.mutate(
            { id: dispatch.runId ?? "", decision: { nodeId, approved } },
            {
              onSuccess: () => toast.success(approved ? t("Approved") : t("Declined")),
              onError: (error) =>
                toast.error(
                  error instanceof Error ? error.message : t("Could not answer the approval"),
                ),
            },
          );

        return (
          <div
            key={dispatch.id}
            className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 p-3"
          >
            <p className="text-body text-text">
              {t("A flow is waiting for approval at {{node}}.", { node: nodeId })}
            </p>
            <div className="flex gap-2">
              <Button size="sm" disabled={approve.isPending} onClick={() => decide(true)}>
                {t("Approve")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={approve.isPending}
                onClick={() => decide(false)}
              >
                {t("Decline")}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
